<template>
  <ProductPageHeader
    v-if="block.type === 'productHero'"
    :author="author"
    :description="block.description"
    :form-url="resolveBlockFormUrl(block.formSlug)"
    :geo-product-slug="geoProductSlug ?? undefined"
    :header-image="block.headerImage"
    :last-update="lastUpdate"
    :parent-breadcrumbs="parentBreadcrumbs"
    :search-title="block.searchTitle"
    :show-companies="block.showCompanies"
    :show-usps="block.showUsps"
    :submit-label="block.submitLabel"
    :title="block.title"
  />
  <lazyContentAccordion
    v-else-if="block.type === 'accordion'"
    :emit-faq-schema="block.emitFaqSchema"
    :icon="block.icon"
    :items="block.items"
  />
  <lazyContentBenefits
    v-else-if="block.type === 'benefits'"
    :color="color"
    :items="block.items"
  />
  <lazyContentButton
    v-else-if="block.type === 'button'"
    :action-type="block.actionType.value"
    :alignment="block.alignment.value"
    :color="block.color.value"
    :href="block.href"
    :label="block.label"
    :prefix-icon="block.prefixIcon"
    :size="block.size.value"
    :suffix-icon="block.suffixIcon"
  />
  <lazyContentButtonList
    v-else-if="block.type === 'buttonList'"
    :action-type="block.actionType.value"
    :alignment="block.alignment.value"
    :button-color="block.color.value"
    :enable-load-more="block.enableLoadMore"
    :geo-product-slug="geoProductSlug"
    :href="block.href ?? undefined"
    :items="block.items ?? []"
    :load-more-threshold="block.loadMoreThreshold ?? undefined"
    :min-columns="block.minColumns ?? undefined"
    :min-width="block.minWidth?.value"
    :preset="block.preset.value"
    :products="block.products ?? []"
    :size="block.size.value"
  />
  <lazyContentColumns
    v-else-if="block.type === 'columns'"
    :alignment="block.alignment?.value"
    :card="block.card"
    :card-background="block.cardBackground?.value"
    :icon-color="block.iconColor?.value"
    :items="block.items"
    :min-width="block.minWidth?.value"
  />
  <ContentCard
    v-else-if="block.type === 'card'"
    :bg-color="block.bgColor.value === 'none' ? undefined : block.bgColor.value"
    :width="block.width.value === 'default' ? undefined : block.width.value"
  >
    <ContentBlockRenderer
      v-for="child in block.content"
      :key="child.id"
      :author
      :block="child"
      :color="cardTextColor(block.bgColor.value)"
      :fallback-form-url
      :geo-product-slug
      :has-local-search
      :last-update
      :parent-breadcrumbs
    />
  </ContentCard>
  <lazyContentCompanyCards
    v-else-if="block.type === 'companyCards'"
    :geo-product-slug="geoProductSlug"
    :limit="block.limit ?? undefined"
  />
  <lazyContentCountryMap
    v-else-if="block.type === 'countryMap'"
    :url="resolveBlockFormUrl(block.formSlug)"
  />
  <lazyContentCtaForm
    v-else-if="block.type === 'ctaForm'"
    :color="color"
    :form-submit-url="resolveBlockFormUrl(block.formSlug) || ''"
    :geo-product-slug="geoProductSlug ?? undefined"
    :go-to-local-search="hasLocalSearch"
    hide-label
    :show-usps="block.showUsps"
    :submit-label="block.submitLabel"
    :title="block.searchTitle ?? undefined"
  />
  <ContentLeadForm
    v-else-if="block.type === 'leadForm'"
    :color="color"
    :form-group-id="block.formGroupId ? Number(block.formGroupId) : null"
    :form-id="block.formId ? Number(block.formId) : null"
  />
  <lazyContentFaq
    v-else-if="block.type === 'faq'"
    :color="color"
    :items="block.items"
  />
  <lazyContentProvinces
    v-else-if="block.type === 'provinces' && geoProductSlug"
    :color="color"
    :geo-product-slug="geoProductSlug"
  />
  <lazyContentRealizedProjects
    v-else-if="block.type === 'realizedProjects' && geoProductSlug"
    :limit="block.perPage"
    :product-slug="geoProductSlug"
  />
  <lazyContentReviews
    v-else-if="block.type === 'reviews' && geoProductSlug"
    :color="color"
    :geo-product-slug="geoProductSlug"
  />
  <lazyContentRichText
    v-else-if="block.type === 'richText'"
    :color="color"
    :content="block.content"
  />
  <lazyContentTextWithImage
    v-else-if="block.type === 'textWithImage'"
    :color="color"
    :content="block.content"
    :image="block.image"
    :image-position="block.imagePosition"
  />
  <ClientOnly v-else-if="block.type === 'customHtml'">
    <lazyContentCustomHtml :css="block.css" :html="block.html" :js="block.js" />
  </ClientOnly>
  <template v-else-if="block.type === 'partialContent'">
    <ContentBlockRenderer
      v-for="child in block.entry?.content ?? []"
      :key="child.id"
      :author
      :block="child"
      :color
      :fallback-form-url
      :geo-product-slug
      :has-local-search
      :last-update
      :parent-breadcrumbs
    />
  </template>
</template>

<script setup lang="ts">
import { ClientOnly } from "#components";
// #app does not re-export this macro.
import { defineLazyHydrationComponent } from "#imports";
import { defineAsyncComponent } from "vue";

import type { ContentBlock } from "~/features/product/api/cms.api.ts";

import ProductPageHeader from "~/features/product/components/ProductPageHeader.vue";
// Nested children → lazy hydration freezes interactive blocks inside.
import ContentCard from "~/shared/components/content-blocks/ContentCard.vue";
import ContentLeadForm from "~/shared/components/content-blocks/ContentLeadForm.vue";
import { ziggy } from "~/shared/libs/ziggy.ts";

/*
  Strategy = the hardest one the block tolerates.

  "never"       — no client state; links delegated to 05.spaLinks. Chunk never fetched on SSR page.
  "interaction" — SSR complete, a click changes it. Unusable with <a> (browser navigates first).
  "visible"     — holds state or animates; must be live as soon as it scrolls in.

  "never" also avoids the post-hydration CSS refetch ({@see nuxt.config.ts} vitalizer comment).
*/

const lazyContentBenefits = defineLazyHydrationComponent(
  "never",
  () => import("~/shared/components/content-blocks/ContentBenefits.vue"),
);
const lazyContentButton = defineLazyHydrationComponent(
  "never",
  () => import("~/shared/components/content-blocks/ContentButton.vue"),
);
const lazyContentRichText = defineLazyHydrationComponent(
  "never",
  () => import("~/shared/components/content-blocks/ContentRichText.vue"),
);
const lazyContentTextWithImage = defineLazyHydrationComponent(
  "never",
  () => import("~/shared/components/content-blocks/ContentTextWithImage.vue"),
);
// No handlers in the tree.
const lazyContentColumns = defineLazyHydrationComponent(
  "never",
  () => import("~/shared/components/content-blocks/ContentColumns.vue"),
);
const lazyContentCountryMap = defineLazyHydrationComponent(
  "never",
  () => import("~/shared/components/content-blocks/ContentCountryMap.vue"),
);
// colada resolves in onServerPrefetch → SSR is final, no show-more state.
const lazyContentProvinces = defineLazyHydrationComponent(
  "never",
  () => import("~/shared/components/content-blocks/ContentProvinces.vue"),
);

// enableLoadMore holds isExpanded; items are <a>.
const lazyContentButtonList = defineLazyHydrationComponent(
  "visible",
  () => import("~/shared/components/content-blocks/ContentButtonList.vue"),
);
// AutoScrollStrip runs a continuous carousel → must be live on paint.
const lazyContentCompanyCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/shared/components/content-blocks/ContentCompanyCards.vue"),
);
// Form + mutation; interaction drops keystrokes before chunk lands.
const lazyContentCtaForm = defineLazyHydrationComponent(
  "visible",
  () => import("~/shared/components/content-blocks/ContentCtaForm.vue"),
);
// Lightbox (65 kB); interaction swallows the first click.
const lazyContentRealizedProjects = defineLazyHydrationComponent(
  "visible",
  () =>
    import("~/shared/components/content-blocks/ContentRealizedProjects.vue"),
);
// Paging + <a> per review; reaches vue-easy-lightbox (31 kB).
const lazyContentReviews = defineLazyHydrationComponent(
  "visible",
  () => import("~/shared/components/content-blocks/ContentReviews.vue"),
);

// Expanding a panel is client state; no links to swallow the click.
const lazyContentAccordion = defineLazyHydrationComponent(
  "interaction",
  () => import("~/shared/components/content-blocks/ContentAccordion.vue"),
);
const lazyContentFaq = defineLazyHydrationComponent(
  "interaction",
  () => import("~/shared/components/content-blocks/ContentFaq.vue"),
);

// ClientOnly → no SSR markup, hydration strategies do not apply.
const lazyContentCustomHtml = defineAsyncComponent(
  () => import("~/shared/components/content-blocks/ContentCustomHtml.vue"),
);

type BreadcrumbLink = { href: string; label: string };

const props = defineProps<{
  author?: string | null;
  block: ContentBlock;
  color?: "gray" | "white";
  fallbackFormUrl?: string | null;
  geoProductSlug?: string | null;
  hasLocalSearch?: boolean;
  lastUpdate: string;
  parentBreadcrumbs?: BreadcrumbLink[];
}>();

function cardTextColor(bgColor: string): "gray" | "white" {
  return bgColor === "primary" ? "white" : "gray";
}

function resolveBlockFormUrl(formSlug: string | null): string | null {
  if (formSlug && props.geoProductSlug) {
    return ziggy("mainSite.geoProduct.leadForm", {
      geoProductSlug: props.geoProductSlug,
      formSlug,
    });
  }
  return props.fallbackFormUrl ?? null;
}
</script>
